/**
 * Mock-icon → real-DS-icon mapping, with the reasoning kept next to it.
 *
 * WHY THIS FILE EXISTS: the Claude Design mock referenced eleven icon names that
 * AbridgeSharedUi does not ship. That is not sloppiness in the mock — the DS icon
 * set (100 names) was built for Abridge's clinical product. It has
 * `patient-visit-summary`, `prior-auth`, `clinical-note` and `single-feedback`,
 * and it has no shield, no gear, no magnifier, no window/browser, no
 * code/terminal and no generic cloud. A developer-platform UI needs a handful of
 * glyphs that simply are not there yet.
 *
 * Two consequences worth knowing:
 *   - Every name below is verified against the DS's `iconMap`. `<Icon>` returns
 *     **null** for an unknown name, so a typo is an invisible gap, not an error.
 *   - `emptyStateIconMap` is a SEPARATE set of 20 illustration names that only
 *     `<EmptyStateIcon>` resolves. `security-data`, `internal-integrity` and
 *     `single-sign-on` live there — passing them to `<Icon>` renders nothing.
 *     That is a real bug this file previously had.
 *
 * Requests for the design system's owners, in priority order:
 *   1. shield / lock  — a security affordance. Substituted per-context below.
 *   2. gear           — general settings. `note-settings` is a document+gear.
 *   3. magnifier      — search. Currently no icon at all in the sidebar.
 *   4. cloud, window, code — to distinguish app template shapes.
 */
import type { IconName } from './ds';

export const icons = {
  /* --- Exact equivalents. Nothing lost. --- */
  yourApps: 'home-filled',
  create: 'plus-circle', // circled rather than bare; the only plus in the set
  notifications: 'bell-filled',
  feedback: 'single-feedback',
  explore: 'chat',
  chevronRight: 'chevron-right',
  arrowLeft: 'arrow-left',
  arrowRight: 'arrow-right',
  available: 'check-circle-filled',
  signIn: 'log-in',
  signOut: 'log-out',
  openInNew: 'open-in-new',
  showValue: 'visibility-on',
  hideValue: 'visibility-off',
  edit: 'edit',
  templateOnboard: 'folder-filled',

  /* --- Close enough. --- */
  docs: 'note-alt', // generic document; clinical-note would imply patient data

  /* --- Compromises. Flagged above; revisit when the DS grows. --- */

  /**
   * No gear exists. `note-settings` is a document-with-gear, so it reads as
   * "settings for a note" rather than "app settings". `filter-parameters` was
   * the alternative and reads as filtering, which is worse here.
   */
  settings: 'note-settings',

  /**
   * "Authorized as yourself" in the wizard is about *whose* identity is being
   * used, so an identity glyph carries the meaning the mock's shield was
   * reaching for better than any status icon would.
   */
  identity: 'account-circle',

  /**
   * The sign-in note explaining that the infra PR uses a separate credential is
   * informational, not a security state — `info-filled` is honest about that.
   * The mock used a shield here; there isn't one.
   */
  info: 'info-filled',

  /**
   * App template shapes. The DS has no code, window or plain-cloud glyph, so
   * these three cannot be told apart pictorially. Rather than pick three
   * unrelated clinical icons and imply a distinction the glyphs do not carry,
   * the two service-shaped templates share one icon and the tag chips plus the
   * title do the differentiating. `folder-filled` for onboarding an existing
   * repo is the one that genuinely reads correctly.
   */
  templateService: 'service-summary',
} satisfies Record<string, IconName>;

/** Template id (from template.yaml metadata.name) → card icon. */
export function templateIcon(templateId: string): IconName {
  if (templateId.includes('onboard')) return icons.templateOnboard;
  return icons.templateService;
}
